/**
 * Origens resolvidas em tempo de execução.
 *
 * Antes, cada chamada montava a URL com `process.env.VUE_APP_*` direto no
 * template. O vue-cli substitui esses acessos por literais durante o build, e
 * o pipeline ainda baixa um `.env` por ambiente antes de buildar, então a
 * origem ficava congelada na imagem: uma imagem por ambiente, e servir o app
 * num ambiente próprio mandava o tráfego do browser para o ambiente antigo.
 * Duas telas nem isso tinham, com o host escrito no meio do código.
 *
 * Agora a origem sai de `config.js`, servido estaticamente ao lado do
 * `index.html` e carregado antes do bundle. Em container,
 * `.docker/docker-entrypoint.sh` reescreve esse arquivo no start a partir das
 * variáveis de ambiente.
 *
 * A configuração de build continua valendo como padrão: sem nada em
 * `config.js`, cada função devolve exatamente o valor que o `process.env.VUE_APP_*`
 * daquele build traria, que é o comportamento anterior.
 */

/** Objeto que `public/config.js` publica antes do bundle carregar. */
const RUNTIME_CONFIG_KEY = '__SELF_HIRING_CONFIG__'

/**
 * Destino do redirecionamento para o produto quando nada está configurado.
 *
 * As duas telas de aceite escreviam este host cada uma do seu jeito, uma com
 * `https://` e barra no fim, outra com `http://` e sem barra. Aqui ele passa a
 * ser um só, na forma segura: `http://` em redirecionamento permite
 * interceptação do tráfego.
 */
const DEFAULT_MAIN_APP_URL = 'https://app.tallos.com.br/'

/**
 * Escolhe entre o valor configurado e o padrão.
 *
 * Só string com conteúdo substitui o padrão: chave ausente chega como
 * `undefined` e chave publicada vazia chega como `''`, e os dois casos
 * significam "não configurado". O retorno vem sem espaços nas pontas, porque o
 * arquivo é gerado por shell a partir de variável de ambiente e uma quebra de
 * linha acidental no fim quebraria toda requisição.
 *
 * @param {unknown} configured valor vindo do `config.js`
 * @param {string|undefined} fallback padrão que já valia neste build
 * @returns {string|undefined}
 */
export function resolveConfigValue(configured, fallback) {
  if (typeof configured !== 'string') {
    return fallback
  }

  const trimmed = configured.trim()

  return trimmed === '' ? fallback : trimmed
}

/**
 * Lê o objeto publicado por `config.js` defensivamente: o arquivo pode não ter
 * sido servido, ou trazer algo que não é objeto.
 *
 * @returns {Record<string, unknown>}
 */
export function readRuntimeConfig() {
  if (typeof window === 'undefined') {
    return {}
  }

  const raw = window[RUNTIME_CONFIG_KEY]

  return typeof raw === 'object' && raw !== null ? raw : {}
}

/**
 * Origem da API de billing intermediária, com a barra no fim que os pontos de
 * chamada esperam, já que concatenam o caminho direto (`${url}term`).
 *
 * Padrão: `VUE_APP_URL_MID_BILLING_API` do build.
 *
 * @returns {string|undefined}
 */
export function billingApiUrl() {
  return resolveConfigValue(
    readRuntimeConfig().billingApiUrl,
    process.env.VUE_APP_URL_MID_BILLING_API,
  )
}

/**
 * Origem da API do RD Billing, sem barra no fim: o único ponto de chamada
 * prepara o caminho com `/accounts/...`.
 *
 * Padrão: `VUE_APP_URL_RD_BILLING_URL_API` do build.
 *
 * @returns {string|undefined}
 */
export function rdBillingApiUrl() {
  return resolveConfigValue(
    readRuntimeConfig().rdBillingApiUrl,
    process.env.VUE_APP_URL_RD_BILLING_URL_API,
  )
}

/**
 * Destino do redirecionamento para o produto, no fim do aceite de termo.
 *
 * Vale para as duas telas de aceite, que antes resolviam esse destino cada uma
 * do seu jeito. Configurar a origem passa a valer para as duas de uma vez.
 *
 * Padrão: `VUE_APP_MAIN_APP_URL` do build e, na falta dele,
 * `DEFAULT_MAIN_APP_URL`.
 *
 * @returns {string}
 */
export function mainAppUrl() {
  return resolveConfigValue(
    readRuntimeConfig().mainAppUrl,
    resolveConfigValue(process.env.VUE_APP_MAIN_APP_URL, DEFAULT_MAIN_APP_URL),
  )
}
